import { ArrowUpCircleIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { EntityIcon } from "@/components/ui/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityType } from "@/queries";

interface UsePromptInTableActionProps {
  isPending: boolean;
  scoreDelta: number;
  smartTableTitle: string;
  onSelect: () => void;
}

export const UsePromptInTableAction = ({
  isPending,
  scoreDelta,
  smartTableTitle,
  onSelect,
}: UsePromptInTableActionProps) => (
  <div className="flex flex-wrap items-center justify-between gap-3 px-1 py-1">
    <div className="flex min-w-0 items-center gap-1.5 text-sm text-gray-600">
      <ArrowUpCircleIcon className="h-4 w-4 shrink-0 text-emerald-600" />
      <span>This Playground prompt performed better than the prompt in</span>
      <span className="inline-flex min-w-0 items-center gap-1 font-medium text-gray-800">
        <EntityIcon type={EntityType.SMART_TABLE} size="xs" className="shrink-0" />
        <span className="truncate">{smartTableTitle}</span>
      </span>
    </div>
    <Button type="button" size="sm" disabled={isPending} onClick={onSelect}>
      {isPending ? "Updating…" : "Use in Table…"}
      <span className="sr-only">Score improved by {scoreDelta}</span>
    </Button>
  </div>
);

interface PromotePromptVersionDialogProps {
  open: boolean;
  isPending: boolean;
  smartTableTitle: string;
  sheetTitle: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

const PromotePromptVersionDialog = ({
  open,
  isPending,
  smartTableTitle,
  sheetTitle,
  onOpenChange,
  onConfirm,
}: PromotePromptVersionDialogProps) => (
  <Dialog
    open={open}
    onOpenChange={nextOpen => {
      if (!isPending) {
        onOpenChange(nextOpen);
      }
    }}
  >
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Use This Prompt in {sheetTitle}?</DialogTitle>
        <DialogDescription className="leading-5">
          This creates a new version of the current Prompt Template and updates the selected Prompt
          Template column in {smartTableTitle} / {sheetTitle}. Existing Table results will need to
          be rerun.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button type="button" disabled={isPending} onClick={onConfirm}>
          {isPending ? "Updating…" : "Use in Table"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default PromotePromptVersionDialog;
